/**
 * Request "deliverables" — what the requester wants Vertiv to produce for a request.
 *
 * The stored values are the **codes** the phase-2 `request_deliverables` CHECK constraint
 * already names (see the PRD addendum, "Deliverables table"). Display strings stay client-side so
 * the codes never appear on screen and copy changes never touch the database.
 *
 * Phase 1 (this POC) persists these client-side via `src/services/deliverablesService.ts`.
 * Phase 2 replaces that service body with API calls; this file does not change.
 */

/**
 * The eight deliverable codes. Must stay in sync with `CK_request_deliverables_item_type`
 * in the PRD addendum, which is the only place that contract lives until the migration is
 * written.
 *
 * Codes are deliberately stable across label changes: the code is what persists, the label is
 * presentation. Every label was rewritten on 2026-07-28 when the business settled the real
 * list, and not one existing code moved, because renaming a code orphans every stored record
 * carrying it and buys nothing the user can see.
 *
 * `TECHNICAL_DRAWING` was retired in that same pass, superseded by the three specific diagram
 * types. A stored record still carrying it renders its raw code via the fallback in
 * `getRequestDeliverableLabel` rather than vanishing.
 */
export type RequestDeliverableType =
  | 'BUDGETARY_QUOTE'
  | 'BOM_ONLY'
  | 'FLOOR_PLAN'
  | 'SINGLE_LINE_DIAGRAM_POWER'
  | 'MONITORING_DIAGRAM'
  | 'FLUID_PIPING_DIAGRAM'
  | 'RENDERING_3D'
  | 'OTHER';

/** Display order for both the submit dialog and the Deliverables step. */
export const REQUEST_DELIVERABLE_TYPES: readonly RequestDeliverableType[] = [
  'BUDGETARY_QUOTE',
  'BOM_ONLY',
  'FLOOR_PLAN',
  'SINGLE_LINE_DIAGRAM_POWER',
  'MONITORING_DIAGRAM',
  'FLUID_PIPING_DIAGRAM',
  'RENDERING_3D',
  'OTHER',
] as const;

/**
 * Business-agreed copy. Punctuation is intentional and inconsistent between entries (an en
 * dash in "Single Line Diagram – Power", a hyphen in "3D Layout - Rendering images") because
 * it matches the list as supplied. Do not "tidy" these without asking.
 */
const REQUEST_DELIVERABLE_LABELS: Record<RequestDeliverableType, string> = {
  BUDGETARY_QUOTE: 'Budgetary Quote',
  BOM_ONLY: 'BOM Proposal only',
  FLOOR_PLAN: 'Equipment Layout/Floor Plan',
  SINGLE_LINE_DIAGRAM_POWER: 'Single Line Diagram – Power',
  MONITORING_DIAGRAM: 'Monitoring Diagram',
  FLUID_PIPING_DIAGRAM: 'Fluid piping Diagram',
  RENDERING_3D: '3D Layout - Rendering images',
  OTHER: 'Other',
};

/**
 * Human-readable label for a deliverable code. Unknown codes fall back to the code itself
 * rather than rendering blank, so a future server-side type is visible instead of silent.
 */
export function getRequestDeliverableLabel(itemType: string): string {
  return (
    REQUEST_DELIVERABLE_LABELS[itemType as RequestDeliverableType] ?? itemType
  );
}

/**
 * One deliverable record. Shaped like the phase-2 `request_deliverables` row (camelCase), so the
 * client contract does not move when the service body is swapped for API calls.
 *
 * There is no `archivedAt`: FR19 (archive on removal) was struck when FR16 made removal a
 * real delete, so a removed deliverable leaves no record and no residue behind it.
 */
export interface RequestDeliverable {
  id: number;
  requestId: string;
  itemType: RequestDeliverableType;
  comment: string | null;
  createdAt: string;
}

/** A deliverable and its comment, before a record exists — the add/edit payload. */
export interface RequestDeliverableEntry {
  itemType: RequestDeliverableType;
  comment: string;
}
